// ============================================
// ShaderProgram — WGSL shader with override constants
// ============================================

import { type IRenderContext } from '../core/IRenderContext';
import {
  type ShaderHandle,
  type PipelineHandle,
  type PipelineDescriptor,
  type OverrideConstants,
  NULL_HANDLE,
} from '../types';

/**
 * Computes a deterministic cache key for override constants.
 * Booleans are normalized to 0/1 for consistent comparison.
 */
export function computeOverrideKey(overrides: OverrideConstants): string {
  const keys = Object.keys(overrides).sort();
  if (keys.length === 0) return '';
  return keys.map(k => {
    const v = overrides[k];
    return `${k}=${typeof v === 'boolean' ? (v ? 1 : 0) : v}`;
  }).join(',');
}

/**
 * ShaderProgram wraps a single (plain, self-contained) WGSL source with:
 *
 * 1. **`override` constants** — WebGPU-native compile-time specialization
 *    set per-pipeline rather than through text preprocessing.
 *
 * ### How it works
 *
 * ```wgsl
 * // In WGSL — use `override` for feature toggles:
 * override USE_SHADOWS: bool = false;
 * override MAX_LIGHTS: u32 = 64;
 *
 * // GPU compiler eliminates dead branches when pipeline is created
 * if (USE_SHADOWS) { ... }
 * ```
 *
 * ```ts
 * const program = new ShaderProgram(ctx, spriteWgslSource);
 *
 * // Get pipeline with specific override constants
 * const pipeline = program.getPipeline(
 *   { USE_SHADOWS: true, MAX_LIGHTS: 128 },
 *   { vertexBuffers: [...], colorTargets: [...], label: 'sprite-lit' },
 * );
 *
 * program.destroy();
 * ```
 */
export class ShaderProgram {
  private ctx: IRenderContext;

  /** Plain WGSL source (Slang-compiled upstream / from getCompiledShader). */
  readonly source: string;

  /** Cached shader handle (one per program). */
  private shaderHandle: ShaderHandle = NULL_HANDLE;

  /**
   * Cached pipeline handles, keyed by `overrideKey|label`.
   * Different override constant sets produce different pipelines but
   * share the same underlying shader module.
   */
  private pipelineCache: Map<string, PipelineHandle> = new Map();

  constructor(ctx: IRenderContext, source: string) {
    this.ctx = ctx;
    this.source = source;
  }

  // -----------------------------------------------------------------------
  // Shader (single module)
  // -----------------------------------------------------------------------

  /**
   * Get (or compile) the shader module.
   *
   * `this.source` is plain WGSL (Slang-compiled upstream / from getCompiledShader). The resulting
   * module is cached for the lifetime of this ShaderProgram.
   */
  getShader(): ShaderHandle {
    if (this.shaderHandle !== NULL_HANDLE) return this.shaderHandle;

    this.shaderHandle = this.ctx.createShader(this.source, this.source);
    return this.shaderHandle;
  }

  // -----------------------------------------------------------------------
  // Pipeline (one per override constants + label)
  // -----------------------------------------------------------------------

  /**
   * Get (or create) a pipeline with specific override constants.
   *
   * @param overrides — WGSL `override` constants (feature toggles, numeric config).
   *   These are set per-pipeline and the GPU compiler eliminates dead code.
   * @param desc — Pipeline descriptor (shader and constants fields will be overwritten).
   */
  getPipeline(
    overrides: OverrideConstants | undefined,
    desc: Omit<PipelineDescriptor, 'shader' | 'constants'>,
  ): PipelineHandle {
    const overrideKey = overrides ? computeOverrideKey(overrides) : '';
    const cacheKey = `${overrideKey}|${desc.label}`;
    let handle = this.pipelineCache.get(cacheKey);
    if (handle !== undefined) return handle;

    const shader = this.getShader();
    handle = this.ctx.createPipeline({
      ...desc,
      shader,
      constants: overrides,
    } as PipelineDescriptor);
    this.pipelineCache.set(cacheKey, handle);
    return handle;
  }

  // -----------------------------------------------------------------------
  // Lifecycle
  // -----------------------------------------------------------------------

  /** Destroy all cached shaders and pipelines. */
  destroy(): void {
    for (const handle of this.pipelineCache.values()) {
      this.ctx.destroyPipeline(handle);
    }
    if (this.shaderHandle !== NULL_HANDLE) {
      this.ctx.destroyShader(this.shaderHandle);
    }
    this.pipelineCache.clear();
    this.shaderHandle = NULL_HANDLE;
  }

  /** Number of cached pipelines (override combos). */
  get pipelineCacheSize(): number {
    return this.pipelineCache.size;
  }
}
